import React from 'react';
import { Clock, Target, TrendingUp, Zap, CheckCircle, BarChart3 } from 'lucide-react';

const Benefits = () => {
  const benefits = [
    {
      icon: Clock,
      title: "Dramatic Time Savings",
      description: "Reduce drawing generation from 3-4 days to just a few hours. Accelerate project timelines and meet deadlines with ease.",
      metric: "90% Time Reduction"
    },
    {
      icon: Target,
      title: "Unmatched Accuracy",
      description: "Minimize human error with automated precision. Our system meticulously crafts every drawing to your specifications, reducing costly revisions.",
      metric: "99%+ Precision"
    },
    {
      icon: TrendingUp,
      title: "Boosted Productivity",
      description: "Free your drafters and designers from repetitive tasks, allowing them to focus on higher-value engineering challenges and innovative solutions.",
      metric: "5x Productivity"
    },
    {
      icon: Zap,
      title: "Faster Submission Times",
      description: "Get your designs to clients and regulatory bodies quicker, improving your competitive edge and project flow.",
      metric: "Instant Delivery"
    },
    {
      icon: CheckCircle,
      title: "Consistent Quality",
      description: "Ensure uniformity and adherence to standards across all your pressure vessel drawings.",
      metric: "100% Standards Compliant"
    },
    {
      icon: BarChart3,
      title: "Scalable Solution",
      description: "A SaaS platform that grows with your business, providing on-demand drawing automation as your needs evolve.",
      metric: "Unlimited Scale"
    }
  ];

  return (
    <section id="benefits" className="py-20 bg-slate-50">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Key Benefits
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Experience the GenieFab Difference: Your Path to Enhanced Productivity
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-slate-100"
                >
                  {/* Icon and Metric */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-slate-700" />
                    </div>
                    <span className="text-sm font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                      {benefit.metric}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-slate-900 mb-4">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Ready to Transform Your Workflow?
              </h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Join engineering teams worldwide who have already revolutionized their pressure vessel design process with GenieFab.
              </p>
              <button 
                className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
                onClick={() => {
                  const element = document.getElementById('pricing');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Start Your Free Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;